import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { deepClone } from '@finra/forms';
import {
  AbstractRedlineService,
  DataCollectionService,
  findField,
  getDeepestRouteSnapshot,
  NavigationData,
  OverlayService,
} from '@finra/rampdi-forms-common';
import { catchError, take, takeUntil } from 'rxjs/operators';
import { EMPTY } from 'rxjs';
import { toTime } from '../utils/dateFormat.utils';

@Injectable({
  providedIn: 'root',
})
export class RedlineService extends AbstractRedlineService {
  private currentTitle: string = 'View All Changes';
  private isIndividualChangesMode: boolean = false;

  constructor(
    public route: ActivatedRoute,
    private DCS: DataCollectionService,
    private OS: OverlayService,
  ) {
    super(route, DCS, OS);
  }

  /**
   * Extends parent class functionality to set dynamic titles for the redline widget
   */
  setTitle(title: string) {
    this.currentTitle = title;
    this.refreshRedlineDataWithTitle();
  }

  /**
   * Extends parent class to add individual changes mode tracking
   */
  setIndividualChangesMode(isIndividual: boolean) {
    this.isIndividualChangesMode = isIndividual;
  }

  /**
   * Gets the current individual changes mode
   */
  getIndividualChangesMode(): boolean {
    return this.isIndividualChangesMode;
  }

  /**
   * Refreshes redline data with current title if redline is open
   */
  private refreshRedlineDataWithTitle() {
    this.redlineDiff$
      .pipe(
        take(1),
        catchError((error) => {
          console.error('Error refreshing redline data with title:', error);
          return EMPTY;
        }),
      )
      .subscribe((currentData) => {
        if (currentData && currentData.open) {
          const updatedData = { ...currentData, title: this.currentTitle };
          this.setRedlineData(updatedData);
        }
      });
  }

  preprocessNavData(navData: NavigationData[]) {
    // Include all navigation items, not just children of the last item
    // This ensures sections that are skipped with subsection children are properly linked
    const allNavItems = [];

    function flattenNavData(items: NavigationData[]) {
      items.forEach((item) => {
        if (!item.skip) {
          allNavItems.push(item);
        }
        if (item.children) {
          // If parent is skipped, prefix children with parent name
          if (item.skip) {
            item.children.forEach((child) => {
              if (!child.skip) {
                allNavItems.push({
                  ...child,
                  text: item.type === 'header' ? child.text : `${item.text} - ${child.text}`,
                });
              } else {
                flattenNavData(item.children);
              }
            });
          } else {
            flattenNavData(item.children);
          }
        }
      });
    }

    flattenNavData(navData);
    // Remove duplicates based on ID
    const uniqueNavItems = allNavItems.filter((item, index, self) => index === self.findIndex((t) => t.id === item.id));
    return uniqueNavItems;
  }

  private readonly EXCLUDED_REDLINE_FIELDS = ['#/creditReportRequest/creditReportTermsOfUse', '#/invoiceId'];

  /**
   * Recursively removes insert diffs with null target from the nested diff tree.
   * These are schema hydration artifacts, not real user changes.
   */
  private removeNullInsertDiffs(obj: any): void {
    if (!obj || typeof obj !== 'object') return;

    for (const key of Object.keys(obj)) {
      const val = obj[key];
      if (val?.decafDiff?.op === 'insert' && val.decafDiff.target == null) {
        delete obj[key];
      } else if (typeof val === 'object') {
        this.removeNullInsertDiffs(val);
      }
    }
  }

  /**
   * For deleted employment history entries where currentEmployment is true,
   * the source has no employmentToDate field. The UI derives "Present" from
   * currentEmployment but the legal view component can't find a diff entry
   * for employmentToDate, so it won't apply strikeout. This injects a
   * synthetic diff so the field is properly struck out.
   */
  private patchEmploymentToDateForDeletedEntries(diff: any): void {
    const employmentHistory = diff?.employmentHistory;
    if (!Array.isArray(employmentHistory)) return;

    employmentHistory.forEach((entry) => {
      if (
        entry?.decafDiff?.op === 'delete' &&
        entry.decafDiff.source?.currentEmployment &&
        !entry.decafDiff.source?.employmentToDate
      ) {
        entry.employmentToDate = {
          decafDiff: {
            op: 'delete',
            source: 'Present',
          },
        };
      }
    });
  }

  /**
   * Sorts redlineData.model.employmentHistory and reorders diff.employmentHistory
   * to match, so the redline view displays in the same sorted order.
   */
  private sortRedlineEmploymentHistory(diff: any, redlineData: any): void {
    const model = redlineData?.model;
    if (!Array.isArray(model?.employmentHistory) || !Array.isArray(diff?.employmentHistory)) return;
    if (model.employmentHistory.length !== diff.employmentHistory.length) return;

    const comparator = (a, b) => {
      const aEnd = toTime(a.model?.employmentToDate);
      const bEnd = toTime(b.model?.employmentToDate);
      const aStart = toTime(a.model?.employmentFromDate);
      const bStart = toTime(b.model?.employmentFromDate);
      if (aEnd !== bEnd) return bEnd - aEnd;
      return bStart - aStart;
    };

    // Pair by matching each diff entry to its model entry via identity fields,
    // so the pairing is correct regardless of prior sort state.
    const usedDiffIndices = new Set<number>();
    const pairs = model.employmentHistory.map((m) => {
      const di = diff.employmentHistory.findIndex((d, i) => {
        if (usedDiffIndices.has(i)) return false;
        const src = d?.decafDiff?.source;
        const tgt = d?.decafDiff?.target;
        // Whole-entry delete/insert: match by source or target fields
        if (src && typeof src === 'object')
          return m.employmentFromDate === src.employmentFromDate && m.employerFirmName === src.employerFirmName;
        if (tgt && typeof tgt === 'object')
          return m.employmentFromDate === tgt.employmentFromDate && m.employerFirmName === tgt.employerFirmName;
        // Per-field diffs: match by resolved model fields against diff's resolved values
        const dFromDate = d?.employmentFromDate?.decafDiff
          ? d.employmentFromDate.decafDiff.target ?? d.employmentFromDate.decafDiff.source
          : d?.employmentFromDate;
        const dFirmName = d?.employerFirmName?.decafDiff
          ? d.employerFirmName.decafDiff.target ?? d.employerFirmName.decafDiff.source
          : d?.employerFirmName;
        return m.employmentFromDate === dFromDate && m.employerFirmName === dFirmName;
      });
      if (di !== -1) usedDiffIndices.add(di);
      return {
        model: m,
        diff: di !== -1 ? diff.employmentHistory[di] : diff.employmentHistory[model.employmentHistory.indexOf(m)],
      };
    });

    pairs.sort(comparator);

    model.employmentHistory = pairs.map((p) => p.model);
    diff.employmentHistory = pairs.map((p) => p.diff);
  }

  classifyDiffData(instanceData, navData, sourceVersion?) {
    const { diff, flattenedDiff } = deepClone(instanceData.redlineData);
    const flattenedDiffFiltered = flattenedDiff;

    this.EXCLUDED_REDLINE_FIELDS.forEach((field) => delete flattenedDiffFiltered[field]);

    //Below is default logic from abstract other than using a filtered FlattenedDiff.
    const cacheKey = `${instanceData.instance.id}:${instanceData.instance.instanceVersion}${sourceVersion ? `:${sourceVersion}` : ''}`;
    if (this.cache[cacheKey]) {
      this.removeNullInsertDiffs(diff);
      this.patchEmploymentToDateForDeletedEntries(diff);
      this.sortRedlineEmploymentHistory(diff, instanceData.redlineData);
      this.setCurrentRedlines(diff);
      return this.cache[cacheKey];
    }
    const dict = this.createflattenedDiffDictionary(flattenedDiffFiltered);
    if (Object.keys(dict).length === 0) {
      const message = this.isIndividualChangesMode
        ? 'No changes made by individual during attestation.'
        : 'No changes have been made to the filing yet';

      this.OS.showSnackbar({ text: message, theme: 'warning' });
      return { open: false };
    }
    // Filter insert diffs where target is null.
    // This occurs when field in not in collectedData and gets hydrated as null.
    // ex. employmentToDate is displayed as PRESENT but does not exist in schema
    this.removeNullInsertDiffs(diff);
    this.patchEmploymentToDateForDeletedEntries(diff);
    this.sortRedlineEmploymentHistory(diff, instanceData.redlineData);
    this.setCurrentRedlines(diff);
    const { schema, labels } = getDeepestRouteSnapshot(this.route).data.schemas;
    const pointers = Object.keys(dict);
    const flattenedDiffTrack = deepClone(dict);
    pointers
      .filter((pointer) => !labels[pointer])
      .forEach((pointer) => {
        const field = findField(
          schema,
          pointer
            .split('/')
            .slice(1)
            .filter((p) => !['200510', '200905'].includes(p)),
        );
        if (field?.title) {
          labels[pointer] = field.title;
        }
      });

    const customLabels = {
      '#/individualInformation/individualAliases': 'Other Names',
      '#/individualInformation/individualPassportIdentifier/lastModified': 'Passport Number',
      '#/individualInformation/individualGovtIDIdentifier/lastModified': 'Government Issued Identification Number',
    };

    pointers.forEach((pointer) => {
      if (customLabels[pointer]) {
        labels[pointer] = customLabels[pointer];
      }
    });
    const result = {
      type: 'redline',
      title: this.currentTitle, // Use dynamic title
      open: true,
      errors: true,
      chips: [
        {
          text: 'Added',
          theme: 'green',
          type: 'insert',
          selected: true,
          get count() {
            return (
              result.sections
                .find((section) => section.id === 'all')
                ?.items.filter((item) => item.type === this.type)
                .reduce((acc, curr) => acc + curr.count ?? 1, 0) ?? 0
            );
          },
        },
        {
          text: 'Changed',
          theme: 'blue',
          type: 'update',
          selected: true,
          get count() {
            return (
              result.sections
                .find((section) => section.id === 'all')
                ?.items.filter((item) => item.type === this.type)
                .reduce((acc, curr) => acc + curr.count ?? 1, 0) ?? 0
            );
          },
        },
        {
          text: 'Deleted',
          theme: 'red',
          type: 'delete',
          selected: true,
          get count() {
            return (
              result.sections
                .find((section) => section.id === 'all')
                ?.items.filter((item) => item.type === this.type)
                .reduce((acc, curr) => acc + curr.count ?? 1, 0) ?? 0
            );
          },
        },
      ],
      get selectedTypes() {
        return this.chips.filter((chip) => chip.selected).map((chip) => chip.type);
      },
      get count() {
        return this.sections.find((section) => section.id === 'all')?.items?.filter((item) => !item.hide).length;
      },
    };
    result.sections = this.preprocessNavData(navData)
      .map(function (section) {
        return {
          id: section.id,
          title: section.text,
          items: pointers
            .filter((pointer) => {
              if (section.id === 'all') {
                return true;
              } else {
                return flattenedDiffTrack[pointer] && section.pointerList?.some((regex) => regex.test(pointer));
              }
            })
            .flatMap((pointer) => {
              if (section.id !== 'all') {
                flattenedDiffTrack[pointer] = undefined;
              }
              return ['insert', 'update', 'delete']
                .filter((type) => dict[pointer][type])
                .map((type) => ({
                  pointer,
                  type,
                  navData: section,
                  text: labels[pointer] ?? pointer,
                  count: dict[pointer][type]?.count ?? 1,
                  get hide() {
                    return !result.selectedTypes.includes(type);
                  },
                }));
            }),
          get count() {
            return this.items.filter((item) => !item.hide).reduce((acc, curr) => acc + curr.count ?? 1, 0);
          },
        };
      })
      .filter((section) => section.count);
    this.cache = { [cacheKey]: result };
    return result;
  }
}
